import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NoAccess from '../../components/NoAccess';
import { useRBAC } from '../../auth/useRBAC';
import { stableDocsSettingsControls } from '../../auth/pageControls';
import { noAccessMessage } from '../../auth/screenAccess';
import { sendErrorMessage } from '../../utils/sendErrorMessage';
import {
  PURPOSES,
  PURPOSE_LABELS,
  Purpose,
  RECORD_TYPES,
  RECORD_TYPE_LABELS,
  RangeKind,
  SUMMARY_LABELS,
  SUMMARY_SECTIONS,
  SettingsUpdate,
  StableDocsSettings as Settings,
  stableDocsService,
} from '../../services/stabledocsService';

const RANGES: { value: RangeKind; label: string }[] = [
  { value: 'last_visit', label: 'Last visit' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom (chosen when sending)' },
];

const MERGE_FIELDS =
  '{patient_name} {barn_name} {owner_name} {recipient_name} {practice_name} {sender_name} {date_range}';

const toggle = <T,>(list: T[], item: T): T[] =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

/** Settings > StableDocs: the wording and defaults the Share Records dialog starts from. */
const StableDocsSettings: React.FC = () => {
  const rbac = useRBAC();
  const can = stableDocsSettingsControls(rbac);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery(['stabledocs', 'settings'], stableDocsService.getSettings, {
    enabled: can.edit,
    staleTime: 60_000,
  });

  const [form, setForm] = useState<Settings | null>(null);
  // A raw draft, so a half-typed number is never rewritten under the cursor.
  const [threshold, setThreshold] = useState('');
  useEffect(() => {
    if (data) {
      setForm(data);
      setThreshold(String(data.attachThresholdMb));
    }
  }, [data]);

  const thresholdNum = Number(threshold);
  // 7 MB is the largest PDF the email service accepts once it is encoded for sending.
  const thresholdOk = Number.isInteger(thresholdNum) && thresholdNum >= 1 && thresholdNum <= 7;

  // Only what the person changed is saved. The form shows the effective values (defaults filled in), and
  // saving those would pin the placeholder wording for this clinic past any later legal review.
  const changes = (): SettingsUpdate => {
    if (!form || !data) return {};
    const out: SettingsUpdate = {};
    for (const k of ['consentText', 'footerText', 'emailDisclaimer'] as const) {
      if (form[k] !== data[k]) out[k] = form[k];
    }
    if (thresholdNum !== data.attachThresholdMb) out.attachThresholdMb = thresholdNum;
    const differs = (a: unknown, b: unknown) => JSON.stringify(a) !== JSON.stringify(b);
    const templates = PURPOSES.filter((p) => differs(form.purposeTemplates[p], data.purposeTemplates[p]));
    if (templates.length) out.purposeTemplates = Object.fromEntries(templates.map((p) => [p, form.purposeTemplates[p]])) as Settings['purposeTemplates'];
    const presets = PURPOSES.filter((p) => differs(form.presets[p], data.presets[p]));
    if (presets.length) out.presets = Object.fromEntries(presets.map((p) => [p, form.presets[p]])) as Settings['presets'];
    return out;
  };
  const dirty = Object.keys(changes()).length > 0;

  const save = useMutation(
    () => stableDocsService.updateSettings(changes()),
    {
      onSuccess: (saved) => {
        queryClient.setQueryData(['stabledocs', 'settings'], saved);
        enqueueSnackbar('StableDocs settings saved', { variant: 'success' });
      },
      onError: (e) => {
        enqueueSnackbar(sendErrorMessage(e, 'Could not save the settings').message, { variant: 'error' });
      },
    },
  );

  if (!can.edit) return <NoAccess dense message={noAccessMessage('StableDocs settings')} />;
  if (isError) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refetch()}>Try again</Button>}>
        Could not load the StableDocs settings. Check the connection and try again.
      </Alert>
    );
  }
  if (isLoading || !form) return <CircularProgress size={22} />;

  const patch = (p: Partial<Settings>) => setForm({ ...form, ...p });
  const setTemplate = (purpose: Purpose, key: 'subject' | 'body', value: string) =>
    patch({ purposeTemplates: { ...form.purposeTemplates, [purpose]: { ...form.purposeTemplates[purpose], [key]: value } } });
  const setPreset = (purpose: Purpose, p: Partial<Settings['presets'][Purpose]>) =>
    patch({ presets: { ...form.presets, [purpose]: { ...form.presets[purpose], ...p } } });

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" fontWeight={700}>StableDocs</Typography>
        <Typography variant="body2" color="text.secondary">
          The wording and defaults used when staff share records.
        </Typography>
      </Box>

      <TextField
        label="Authorization text"
        helperText="Shown when sending to someone other than the owner or a vet."
        value={form.consentText}
        onChange={(e) => patch({ consentText: e.target.value })}
        multiline
        minRows={2}
        fullWidth
      />
      <TextField
        label="PDF footer"
        helperText="Printed at the bottom of every page."
        value={form.footerText}
        onChange={(e) => patch({ footerText: e.target.value })}
        multiline
        minRows={2}
        fullWidth
      />
      <TextField
        label="Email disclaimer"
        value={form.emailDisclaimer}
        onChange={(e) => patch({ emailDisclaimer: e.target.value })}
        multiline
        minRows={2}
        fullWidth
      />
      <TextField
        label="Largest PDF to email (MB)"
        helperText="Whole number from 1 to 7."
        value={threshold}
        onChange={(e) => setThreshold(e.target.value)}
        error={!thresholdOk}
        inputProps={{ inputMode: 'numeric' }}
        sx={{ maxWidth: 260 }}
      />

      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>By purpose</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Email wording and the defaults chosen when a purpose is picked. Merge fields: {MERGE_FIELDS}
        </Typography>
        {PURPOSES.map((purpose) => {
          const preset = form.presets[purpose];
          const tpl = form.purposeTemplates[purpose];
          return (
            <Accordion key={purpose} disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{PURPOSE_LABELS[purpose]}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    label="Email subject"
                    value={tpl.subject}
                    onChange={(e) => setTemplate(purpose, 'subject', e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ 'aria-label': `${PURPOSE_LABELS[purpose]} email subject` }}
                  />
                  <TextField
                    label="Email message"
                    value={tpl.body}
                    onChange={(e) => setTemplate(purpose, 'body', e.target.value)}
                    multiline
                    minRows={5}
                    fullWidth
                    size="small"
                    inputProps={{ 'aria-label': `${PURPOSE_LABELS[purpose]} email message` }}
                  />
                  <TextField
                    select
                    label="Dates"
                    value={preset.range}
                    onChange={(e) => setPreset(purpose, { range: e.target.value as RangeKind })}
                    size="small"
                    sx={{ maxWidth: 300 }}
                  >
                    {RANGES.map((r) => (
                      <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                  </TextField>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Records</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>
                      {RECORD_TYPES.map((t) => (
                        <FormControlLabel
                          key={t}
                          control={
                            <Checkbox
                              size="small"
                              checked={preset.types.includes(t)}
                              onChange={() => setPreset(purpose, { types: toggle(preset.types, t) })}
                            />
                          }
                          label={RECORD_TYPE_LABELS[t]}

                        />
                      ))}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Summary page</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>
                      {SUMMARY_SECTIONS.map((s) => (
                        <FormControlLabel
                          key={s}
                          control={
                            <Checkbox
                              size="small"
                              checked={preset.summarySections.includes(s)}
                              onChange={() => setPreset(purpose, { summarySections: toggle(preset.summarySections, s) })}
                            />
                          }
                          label={SUMMARY_LABELS[s]}

                        />
                      ))}
                    </Box>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      <Box>
        <Button variant="contained" onClick={() => save.mutate()} disabled={!thresholdOk || !dirty || save.isLoading} data-testid="stabledocs-save">
          Save
        </Button>
      </Box>
    </Stack>
  );
};

export default StableDocsSettings;
